// /admin/ops/trades/$tradeId — trade integrity 3-column view for
// matching findings. Shows settlement_trades + order_fills +
// transactions side-by-side so the operator can spot which side
// disagrees.
import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { Skeleton } from "@/components/ui/Skeleton";
import { fetchTradeIntegrity, opsKeys } from "@/lib/ops-queries";
import type {
  FillSide,
  LedgerSide,
  SettlementSide,
} from "@/lib/ops-types";

export const Route = createFileRoute("/_app/ops/trades/$tradeId")({
  component: TradeIntegrity,
});

function TradeIntegrity() {
  const { tradeId } = Route.useParams();
  const q = useQuery({
    queryKey: opsKeys.tradeIntegrity(tradeId),
    queryFn: () => fetchTradeIntegrity(tradeId),
  });

  if (q.isLoading) return <Skeleton className="h-64" />;
  if (q.isError || !q.data) return <div className="text-red-500">Trade not found.</div>;
  const ti = q.data;

  return (
    <div className="space-y-4">
      <Link to="/ops/findings" className="text-sm text-muted hover:text-fg">← Back to inbox</Link>
      <h2 className="font-display text-xl">
        Trade integrity — <code className="font-mono">{ti.TradeID}</code>
      </h2>

      <div className="grid gap-4 lg:grid-cols-3">
        <SettlementColumn rows={ti.Settlements} />
        <FillColumn rows={ti.Fills} />
        <LedgerColumn rows={ti.LedgerTxns} />
      </div>
    </div>
  );
}

function SettlementColumn({ rows }: { rows: SettlementSide[] }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Settlement ({rows.length})</CardTitle>
      </CardHeader>
      <CardBody>
        {rows.length === 0 && <div className="text-sm text-muted">missing — expected 2 rows</div>}
        {rows.map((r) => (
          <div key={r.TradeRecordID} className="mb-3 space-y-1 text-xs">
            <div className="font-mono">{r.IsMaker ? "maker" : "taker"} · {r.Side} · order {r.OrderID}</div>
            <div className="text-muted">price <b className="text-fg">{r.Price.toLocaleString()}</b> qty <b className="text-fg">{r.Quantity.toLocaleString()}</b></div>
            <div className="text-muted">net_settled <b className="text-fg">{r.NetSettled.toLocaleString()}</b> fee <b className="text-fg">{r.FeeAmount.toLocaleString()}</b></div>
            <div className="text-muted">ledger_status <b className="text-fg">{r.LedgerStatus}</b></div>
          </div>
        ))}
      </CardBody>
    </Card>
  );
}

function FillColumn({ rows }: { rows: FillSide[] }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Order fills ({rows.length})</CardTitle>
      </CardHeader>
      <CardBody>
        {rows.length === 0 && <div className="text-sm text-muted">missing — expected 2 rows</div>}
        {rows.map((r) => (
          <div key={r.FillID} className="mb-3 space-y-1 text-xs">
            <div className="font-mono">{r.IsMaker ? "maker" : "taker"} · order {r.OrderID}</div>
            <div className="text-muted">price <b className="text-fg">{r.Price.toLocaleString()}</b> qty <b className="text-fg">{r.Quantity.toLocaleString()}</b></div>
          </div>
        ))}
      </CardBody>
    </Card>
  );
}

function LedgerColumn({ rows }: { rows: LedgerSide[] }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Ledger txns ({rows.length})</CardTitle>
      </CardHeader>
      <CardBody>
        {rows.length === 0 && <div className="text-sm text-muted">none (no settlement was COMMITTED)</div>}
        {rows.map((r) => (
          <div key={r.TransactionID} className="mb-3 space-y-1 text-xs">
            <div className="font-mono">{r.Kind} · {r.OperationID}</div>
            <div className="text-muted">amount <b className="text-fg">{r.Amount.toLocaleString()}</b> · status <b className="text-fg">{r.Status}</b></div>
            <div className="text-muted">wallet {r.WalletID}</div>
          </div>
        ))}
      </CardBody>
    </Card>
  );
}
